// mission-initializer-worktree.ts
//
// Tạo worktree CÁCH LY cho mission mà KHÔNG đụng checkout của người dùng (§9.4, F09).
//
// Bất biến: sau khi hàm này chạy, `git status` và `git rev-parse HEAD` của SOURCE repo
// phải y hệt trước đó. Ta chỉ TẠO ref mới (`refs/heads/autopilot/<mission>`) và thêm
// một worktree — không checkout, không đổi HEAD, không đụng nhánh đang mở. HEAD của
// WORKTREE gắn vào nhánh mission (SOL-01) để commit của mission luôn tiến ref đó.
//
// Repo CHƯA CÓ HEAD (vừa `git init`, 0 commit): không thể `worktree add` từ HEAD chưa
// sinh. Cách làm đúng là plumbing:
//   git commit-tree <empty-tree> -m ...   → tạo commit rỗng KHÔNG gắn vào đâu cả
//   git update-ref refs/heads/autopilot/<m> <sha>  → gắn vào ĐÚNG ref mission
// HEAD của source vẫn unborn sau đó (test 42).

import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";

/** SHA của cây rỗng — hằng số của định dạng object git (SHA-1), không phải cấu hình. */
export const EMPTY_TREE_SHA = "4b825dc642cb6eb9a060e54bf8d69288fbee4904";

export interface WorktreeInitResult {
  worktreePath: string;
  branchRef: string;
  baseSha: string;
  createdEmptyBaseCommit: boolean;
}

export type GitRunner = (
  args: string[],
  cwd: string,
) => Promise<{ status: number | null; stdout: string; stderr: string }>;

export const defaultRunner: GitRunner = async (args, cwd) => {
  const res = await spawnAsyncCapture("git", args, {
    cwd,
    timeoutMs: 60_000,
    forwardSigint: false,
  });
  return { status: res.status, stdout: res.stdout, stderr: res.stderr };
};

async function git(runner: GitRunner, args: string[], cwd: string): Promise<string> {
  const res = await runner(args, cwd);
  if (res.status !== 0) {
    throw new Error(`git ${args.join(" ")} thất bại (${res.status}): ${res.stderr.trim()}`);
  }
  return res.stdout.trim();
}

/** Repo đã có commit nào chưa (HEAD đã "sinh" chưa). */
export async function hasHead(
  sourceRepo: string,
  runner: GitRunner = defaultRunner,
): Promise<boolean> {
  const res = await runner(["rev-parse", "--verify", "HEAD"], sourceRepo);
  return res.status === 0;
}

/** Short branch name — what `worktree add` needs to ATTACH HEAD (see initMissionWorktree). */
export function missionBranchName(missionId: string): string {
  return `autopilot/${missionId}`;
}

export function missionBranchRef(missionId: string): string {
  return `refs/heads/${missionBranchName(missionId)}`;
}

/**
 * Tạo worktree mission. `worktreePath` phải CHƯA tồn tại (git tự tạo). Ref mission
 * trỏ vào `base_sha` của request khi có (mặc định HEAD nếu không truyền).
 *
 * Không bao giờ: `git checkout`, `git switch`, `git commit` trong source; không sửa
 * HEAD của source. Chỉ `update-ref` lên ref RIÊNG của mission + `worktree add`.
 */
export async function initMissionWorktree(
  sourceRepo: string,
  worktreePath: string,
  missionId: string,
  runner: GitRunner = defaultRunner,
  /** sha đã validate ở preflight (mission-initializer-preflight.ts); bỏ qua khi repo chưa có HEAD (unborn -> null-oid path, không đổi). */
  requestedBaseSha?: string,
): Promise<WorktreeInitResult> {
  const branchRef = missionBranchRef(missionId);
  const headExists = await hasHead(sourceRepo, runner);
  let createdEmptyBaseCommit = false;
  let baseSha: string;

  if (headExists) {
    baseSha = requestedBaseSha ?? (await git(runner, ["rev-parse", "HEAD"], sourceRepo));
    await git(runner, ["update-ref", branchRef, baseSha], sourceRepo);
  } else {
    // Repo TRỐNG: dựng base commit rỗng bằng plumbing rồi gắn vào ref mission.
    // `commit-tree` KHÔNG đụng HEAD, KHÔNG cần index, KHÔNG cần checkout.
    baseSha = await git(
      runner,
      ["commit-tree", EMPTY_TREE_SHA, "-m", `autopilot: base rỗng cho mission ${missionId}`],
      sourceRepo,
    );
    await git(runner, ["update-ref", branchRef, baseSha], sourceRepo);
    createdEmptyBaseCommit = true;
  }

  // SOL-01 (audit 2026-09-28): the SHORT branch name, never `branchRef`. Given a
  // full ref (`refs/heads/...`) git checks out a DETACHED HEAD: commit-owner then
  // advances HEAD while the mission ref stays at base, and `archive` (worktree
  // remove) leaves every mission commit reachable from no ref at all.
  await git(runner, ["worktree", "add", worktreePath, missionBranchName(missionId)], sourceRepo);
  return { worktreePath, branchRef, baseSha, createdEmptyBaseCommit };
}

/**
 * SOL-01: make the worktree's final HEAD reachable from a ref BEFORE the worktree
 * is removed. HEAD attached to a branch ⇒ already kept. Detached (a mission
 * init'd before the fix above) ⇒ fast-forward the mission ref when it is an
 * ancestor of HEAD; otherwise keep HEAD under its own ref so neither commit is
 * dropped. Returns the ref holding HEAD, or `undefined` when HEAD is unborn.
 * Throws when a ref cannot be written — the caller must not remove the worktree.
 */
export async function preserveWorktreeHeadOnMissionRef(
  worktreePath: string,
  missionId: string,
  runner: GitRunner = defaultRunner,
): Promise<string | undefined> {
  const head = await runner(["rev-parse", "--verify", "-q", "HEAD"], worktreePath);
  if (head.status !== 0) return undefined;
  const headSha = head.stdout.trim();

  const attached = await runner(["symbolic-ref", "-q", "HEAD"], worktreePath);
  if (attached.status === 0) return attached.stdout.trim();

  const branchRef = missionBranchRef(missionId);
  const current = await runner(["rev-parse", "--verify", "-q", branchRef], worktreePath);
  const refSha = current.status === 0 ? current.stdout.trim() : undefined;
  if (refSha === headSha) return branchRef;
  const refIsAncestor =
    refSha === undefined ||
    (await runner(["merge-base", "--is-ancestor", refSha, headSha], worktreePath)).status === 0;
  const keepRef = refIsAncestor ? branchRef : `${branchRef}-final-${headSha.slice(0, 12)}`;
  await git(runner, ["update-ref", keepRef, headSha], worktreePath);
  return keepRef;
}

/**
 * Gỡ worktree mission (dọn dẹp cuối mission / test). Giữ ref nhánh mission lại để còn
 * truy vết — xoá ref là quyết định của con người, không phải của dọn dẹp tự động.
 */
export async function removeMissionWorktree(
  sourceRepo: string,
  worktreePath: string,
  runner: GitRunner = defaultRunner,
): Promise<void> {
  await runner(["worktree", "remove", "--force", worktreePath], sourceRepo);
  await runner(["worktree", "prune"], sourceRepo);
}
